import React, { useContext, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Swal from "sweetalert2";
import { motion } from "framer-motion";

import {
    Box,
    Typography,
    TextField,
    InputAdornment,
    IconButton,
    Button,
    CircularProgress,
    useMediaQuery,
} from "@mui/material";

import {
    Email,
    Lock,
    Visibility,
    VisibilityOff,
    ArrowBack,
    VerifiedUser,
    School,
    AutoStories,
    WorkspacePremium,
    Groups,
    Lightbulb,
    BusinessCenter,
    VolunteerActivism,
    ArrowForward,
    MenuBook,
    EmojiObjects,
    CheckCircle,
    TrendingUp,
} from "@mui/icons-material";

import api from "../api/api";
import { AuthContext } from "../AuthContext";

/* =========================================================
   PATIF BRAND COLORS
========================================================= */

const COLORS = {
    navy: "#031B35",
    deepNavy: "#021426",
    navyBlue: "#062A4B",
    blue: "#063A6B",
    blueLight: "#0F6DAB",

    black: "#071018",

    gold: "#F6C944",
    goldLight: "#FFE58A",
    yellow: "#EFA81C",
    orange: "#C0750D",

    green: "#16803A",
    greenLight: "#4FAE22",

    white: "#FFFFFF",
    cream: "#FBF8EF",
    ivory: "#FFFDF7",

    text: "#152330",
    muted: "#6B7780",

    border: "#E2E5E0",
    paleGold: "#FFF8DC",
    paleBlue: "#EEF5FA",
    paleNavy: "#EAF0F5",

    danger: "#B42318",
};

/* =========================================================
   BRAND LOGO
========================================================= */

const BRAND_LOGO =
    "/static/media/passionaAidsLogo.56c161103470b5b22e04.jpg";

/* =========================================================
   FLOATING INSTITUTIONAL ICON
========================================================= */

const FloatingInstitutionIcon = ({
    Icon,
    top,
    left,
    right,
    bottom,
    delay = 0,
    size = 28,
}) => (
    <motion.div
        initial={{
            opacity: 0,
            y: 25,
            scale: 0.75,
        }}
        animate={{
            opacity: [0, 0.6, 0.35, 0],
            y: [25, -5, -30, -65],
            x: [0, 8, -5, 12],
            rotate: [0, 8, -6, 12],
            scale: [0.75, 1, 1.05, 0.65],
        }}
        transition={{
            duration: 9,
            delay,
            repeat: Infinity,
            ease: "easeInOut",
        }}
        style={{
            position: "absolute",
            top,
            left,
            right,
            bottom,
            zIndex: 3,
            pointerEvents: "none",
        }}
    >
        <Icon
            sx={{
                fontSize: size,
                color: COLORS.gold,
                opacity: 0.55,
                filter:
                    "drop-shadow(0 0 14px rgba(246,201,68,.22))",
            }}
        />
    </motion.div>
);

/* =========================================================
   BACKGROUND GLOW
========================================================= */

const Glow = ({
    size = 400,
    top,
    left,
    right,
    bottom,
    color = "rgba(246,201,68,.13)",
}) => (
    <motion.div
        animate={{
            scale: [0.92, 1.08, 0.92],
            opacity: [0.35, 0.65, 0.35],
        }}
        transition={{
            duration: 9,
            repeat: Infinity,
            ease: "easeInOut",
        }}
        style={{
            position: "absolute",
            width: size,
            height: size,
            top,
            left,
            right,
            bottom,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${color} 0%, transparent 70%)`,
            pointerEvents: "none",
            zIndex: 1,
        }}
    />
);

/* =========================================================
   INSTITUTIONAL GRID
========================================================= */

const InstitutionalGrid = () => (
    <Box
        sx={{
            position: "absolute",
            inset: 0,
            opacity: 0.065,
            pointerEvents: "none",
            backgroundImage: `
                linear-gradient(rgba(255,255,255,.55) 1px, transparent 1px),
                linear-gradient(90deg, rgba(255,255,255,.55) 1px, transparent 1px)
            `,
            backgroundSize: "48px 48px",
            maskImage:
                "linear-gradient(to bottom, black, transparent 90%)",
        }}
    />
);

/* =========================================================
   LOGIN COMPONENT
========================================================= */

const Login = () => {
    const navigate = useNavigate();
    const { setUser } = useContext(AuthContext);

    const isMobile = useMediaQuery("(max-width:900px)");

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    /* =====================================================
       ROLE REDIRECTION
    ===================================================== */

    const redirectUser = (user) => {
        const role = String(
            user?.user_type || user?.role || ""
        )
            .toLowerCase()
            .trim();

        switch (role) {
            case "owner":
                navigate("/owner-dashboard");
                break;

            case "admin":
                navigate("/newAdminDashboard");
                break;

            case "teacher":
                navigate("/teacher-dashboard");
                break;

            case "student":
                navigate("/student-dashboard");
                break;

            case "parent":
                navigate("/parentsDashboard");
                break;

            case "registry":
                navigate("/registerDashboard");
                break;

            case "principal":
                navigate("/principal-dashboard");
                break;

            case "proprietor":
                navigate("/proprietor-dashboard");
                break;

            case "vpi":
                navigate("/vpi-dashboard");
                break;

            case "vpa":
                navigate("/newVpaDashboard");
                break;

            case "it personel":
            case "it personnel":
                navigate("/it-dashboard");
                break;

            case "business_manager":
            case "business manager":
                navigate("/businessManagerDashboard");
                break;

            case "admission officer":
            case "admission_officer":
                navigate("/admissionOfficialDashboard");
                break;

            case "dean":
                navigate("/dean-dashboard");
                break;

            default:
                navigate("/");
        }
    };

    /* =====================================================
       CLEAR AUTHENTICATION
    ===================================================== */

    const clearAuthenticationData = () => {
        localStorage.removeItem("access");
        localStorage.removeItem("refresh");
        localStorage.removeItem("user");
        localStorage.removeItem("employment_status");
        localStorage.removeItem("employment_status_display");
        localStorage.removeItem("employment_academic_year");
        localStorage.removeItem("auth_email");
    };

    /* =====================================================
       LOGIN ERROR
    ===================================================== */

    const getLoginError = (error) => {
        const responseData = error?.response?.data;

        if (responseData?.detail) {
            return String(responseData.detail);
        }

        if (responseData?.message) {
            return String(responseData.message);
        }

        if (typeof responseData === "string") {
            return responseData;
        }

        if (
            responseData &&
            typeof responseData === "object"
        ) {
            const messages = [];

            Object.entries(responseData).forEach(
                ([field, value]) => {
                    if (field === "detail") {
                        return;
                    }

                    if (Array.isArray(value)) {
                        value.forEach((item) => {
                            if (
                                item !== null &&
                                item !== undefined
                            ) {
                                messages.push(String(item));
                            }
                        });
                    } else if (
                        typeof value === "string"
                    ) {
                        messages.push(value);
                    }
                }
            );

            if (messages.length) {
                return messages.join("\n");
            }
        }

        return "Invalid email or password.";
    };

    /* =====================================================
       EMPLOYMENT STATUS ALERTS
    ===================================================== */

    const showEmploymentStatusAlert = (data) => {
        const errorCode = String(
            data?.error_code || ""
        )
            .toUpperCase()
            .trim();

        const status = String(
            data?.employment_status || ""
        )
            .toLowerCase()
            .trim();

        const academicYear =
            data?.academic_year ||
            data?.academic_year_name ||
            "the current academic year";

        const statusDisplay =
            data?.employment_status_display ||
            (status === "fired"
                ? "SACKED / FIRED"
                : status === "removed"
                    ? "REMOVED"
                    : "");

        if (
            errorCode === "TEACHER_FIRED" ||
            errorCode === "EMPLOYMENT_FIRED" ||
            status === "fired"
        ) {
            Swal.fire({
                icon: "error",
                title: "Account Access Restricted",
                html: `
                    <div style="text-align:center;">
                        <p style="
                            font-size:15px;
                            line-height:1.7;
                            color:#344054;
                        ">
                            Your account cannot currently be used
                            to access the PASSION AIDS THE FUTURE
                            institutional portal.
                        </p>

                        <div style="
                            display:inline-block;
                            padding:9px 17px;
                            border-radius:30px;
                            background:#FEF2F2;
                            color:#B42318;
                            font-weight:800;
                            margin:12px 0 15px;
                            font-size:12px;
                            letter-spacing:.5px;
                        ">
                            EMPLOYMENT STATUS:
                            ${statusDisplay || "SACKED / FIRED"}
                        </div>

                        <p style="
                            color:#667085;
                            line-height:1.7;
                            font-size:14px;
                        ">
                            Your most recent employment record
                            indicates that your employment has
                            been terminated.
                        </p>

                        <p style="
                            color:#344054;
                            font-weight:600;
                            line-height:1.7;
                            font-size:14px;
                        ">
                            Please contact an authorized
                            PATIF administrator for assistance.
                        </p>

                        <p style="
                            color:#667085;
                            font-size:12px;
                            margin-top:14px;
                        ">
                            Latest employment record:
                            <strong>${academicYear}</strong>
                        </p>
                    </div>
                `,
                confirmButtonText: "Understood",
                confirmButtonColor: COLORS.navyBlue,
                background: COLORS.white,
            });

            return true;
        }

        if (
            errorCode === "TEACHER_REMOVED" ||
            errorCode === "EMPLOYMENT_REMOVED" ||
            status === "removed"
        ) {
            Swal.fire({
                icon: "warning",
                title: "Account Access Restricted",
                html: `
                    <div style="text-align:center;">
                        <p style="
                            font-size:15px;
                            line-height:1.7;
                            color:#344054;
                        ">
                            Your account cannot currently be used
                            to access the PASSION AIDS THE FUTURE
                            institutional portal.
                        </p>

                        <div style="
                            display:inline-block;
                            padding:9px 17px;
                            border-radius:30px;
                            background:#FFF8DC;
                            color:#8A6200;
                            font-weight:800;
                            margin:12px 0 15px;
                            font-size:12px;
                            letter-spacing:.5px;
                        ">
                            EMPLOYMENT STATUS:
                            ${statusDisplay || "REMOVED"}
                        </div>

                        <p style="
                            color:#667085;
                            line-height:1.7;
                            font-size:14px;
                        ">
                            Your most recent employment record
                            indicates that you have been removed
                            from the institution.
                        </p>

                        <p style="
                            color:#344054;
                            font-weight:600;
                            line-height:1.7;
                            font-size:14px;
                        ">
                            Please contact an authorized
                            PATIF administrator for assistance.
                        </p>

                        <p style="
                            color:#667085;
                            font-size:12px;
                            margin-top:14px;
                        ">
                            Latest employment record:
                            <strong>${academicYear}</strong>
                        </p>
                    </div>
                `,
                confirmButtonText: "Understood",
                confirmButtonColor: COLORS.navyBlue,
                background: COLORS.white,
            });

            return true;
        }

        if (errorCode === "TEACHER_NOT_ASSIGNED") {
            Swal.fire({
                icon: "warning",
                title: "Instructor Not Assigned",
                html: `
                    <div style="text-align:center;">
                        <p style="
                            font-size:15px;
                            line-height:1.7;
                            color:#344054;
                        ">
                            Your instructor account is not currently
                            assigned to an active academic year.
                        </p>

                        <div style="
                            margin:14px auto;
                            padding:12px 18px;
                            max-width:270px;
                            border-radius:12px;
                            background:#EEF5FA;
                            color:#063A6B;
                            font-size:16px;
                            font-weight:900;
                        ">
                            ${academicYear}
                        </div>

                        <p style="
                            color:#667085;
                            line-height:1.7;
                            font-size:14px;
                        ">
                            Please contact PATIF administration
                            to have your instructor record assigned.
                        </p>
                    </div>
                `,
                confirmButtonText: "Understood",
                confirmButtonColor: COLORS.navyBlue,
            });

            return true;
        }

        if (errorCode === "NO_ACTIVE_ACADEMIC_YEAR") {
            Swal.fire({
                icon: "warning",
                title: "No Active Academic Year",
                text:
                    "There is currently no active academic year. Please contact PASSION AIDS THE FUTURE administration.",
                confirmButtonText: "Understood",
                confirmButtonColor: COLORS.navyBlue,
                background: COLORS.white,
            });

            return true;
        }

        if (
            errorCode === "EMPLOYMENT_LOGIN_BLOCKED" ||
            errorCode === "LOGIN_BLOCKED"
        ) {
            Swal.fire({
                icon: "warning",
                title: "Login Blocked",
                html: `
                    <div style="text-align:center;">
                        <p style="
                            font-size:15px;
                            line-height:1.7;
                            color:#344054;
                        ">
                            Your account is currently blocked
                            from accessing the PATIF institutional
                            portal.
                        </p>

                        <p style="
                            color:#344054;
                            font-weight:600;
                            line-height:1.7;
                            font-size:14px;
                        ">
                            Please contact an authorized
                            administrator for assistance.
                        </p>
                    </div>
                `,
                confirmButtonText: "Understood",
                confirmButtonColor: COLORS.navyBlue,
                background: COLORS.white,
            });

            return true;
        }

        return false;
    };

    /* =====================================================
       BLOCKED RESPONSE
    ===================================================== */

    const handleBlockedResponse = (data) => {
        if (!data) {
            return false;
        }

        const status = String(
            data?.employment_status || ""
        )
            .toLowerCase()
            .trim();

        const errorCode = String(
            data?.error_code || ""
        )
            .toUpperCase()
            .trim();

        const employmentCodes = [
            "TEACHER_REMOVED",
            "TEACHER_FIRED",
            "EMPLOYMENT_REMOVED",
            "EMPLOYMENT_FIRED",
            "EMPLOYMENT_LOGIN_BLOCKED",
            "LOGIN_BLOCKED",
            "TEACHER_NOT_ASSIGNED",
            "NO_ACTIVE_ACADEMIC_YEAR",
        ];

        const isEmploymentError =
            employmentCodes.includes(errorCode) ||
            ["removed", "fired"].includes(status);

        if (!isEmploymentError) {
            return false;
        }

        clearAuthenticationData();

        return showEmploymentStatusAlert(data);
    };

    /* =====================================================
       LOGIN SUBMIT
    ===================================================== */

    const handleSubmit = async (e) => {
        e.preventDefault();

        const cleanEmail = email.trim();

        if (!cleanEmail || !password) {
            Swal.fire({
                icon: "warning",
                title: "Missing Information",
                text:
                    "Please enter your email address and password.",
                confirmButtonColor: COLORS.navyBlue,
                background: COLORS.white,
            });

            return;
        }

        setLoading(true);

        try {
            clearAuthenticationData();

            const response = await api.post(
                "/api/token/",
                {
                    email: cleanEmail,
                    password,
                }
            );

            console.log(
                "TOKEN RESPONSE:",
                response.data
            );

            if (
                handleBlockedResponse(
                    response.data
                )
            ) {
                return;
            }

            const role = String(
                response.data?.user_type ||
                response.data?.user?.role ||
                ""
            )
                .toLowerCase()
                .trim();

            const authenticationRoles = [
                "admin",
                "registry",
                "principal",
                "proprietor",
                "business_manager",
                "business manager",
                "vpi",
                "vpa",
                "it personel",
                "it personnel",
            ];

            if (
                response.data?.requires_authentication &&
                authenticationRoles.includes(role)
            ) {
                const authEmail =
                    response.data?.email ||
                    response.data?.user?.email ||
                    cleanEmail;

                localStorage.setItem(
                    "auth_email",
                    authEmail
                );

                navigate(
                    "/loginAuthentication",
                    {
                        state: {
                            email: authEmail,
                        },
                    }
                );

                return;
            }

            const access =
                response.data?.access;

            const refresh =
                response.data?.refresh;

            if (!access || !refresh) {
                throw new Error(
                    "Authentication tokens were not returned."
                );
            }

            localStorage.setItem(
                "access",
                access
            );

            localStorage.setItem(
                "refresh",
                refresh
            );

            if (
                response.data?.employment_status
            ) {
                localStorage.setItem(
                    "employment_status",
                    response.data.employment_status
                );
            }

            if (
                response.data?.employment_status_display
            ) {
                localStorage.setItem(
                    "employment_status_display",
                    response.data
                        .employment_status_display
                );
            }

            if (
                response.data?.academic_year
            ) {
                localStorage.setItem(
                    "employment_academic_year",
                    response.data.academic_year
                );
            }

            window.dispatchEvent(
                new Event("user-logged-in")
            );

            let profileResponse;

            try {
                profileResponse =
                    await api.get(
                        "/api/user-profile/"
                    );
            } catch (profileError) {
                clearAuthenticationData();
                throw profileError;
            }

            const user =
                profileResponse.data;

            const frontendStatus = String(
                response.data
                    ?.employment_status ||
                user?.employment_status ||
                ""
            )
                .toLowerCase()
                .trim();

            if (
                frontendStatus === "removed" ||
                frontendStatus === "fired"
            ) {
                clearAuthenticationData();

                showEmploymentStatusAlert({
                    error_code:
                        frontendStatus === "removed"
                            ? "TEACHER_REMOVED"
                            : "TEACHER_FIRED",
                    employment_status:
                        frontendStatus,
                    employment_status_display:
                        frontendStatus === "removed"
                            ? "REMOVED"
                            : "SACKED / FIRED",
                    academic_year:
                        response.data?.academic_year ||
                        response.data
                            ?.academic_year_name,
                });

                return;
            }

            setUser(user);

            localStorage.setItem(
                "user",
                JSON.stringify(user)
            );

            Swal.fire({
                icon: "success",
                title: "Welcome Back!",
                text: `Welcome ${
                    user?.user_type ||
                    user?.role ||
                    ""
                }`,
                timer: 1800,
                showConfirmButton: false,
                background: COLORS.white,
                color: COLORS.text,
                iconColor: COLORS.green,
            });

            setTimeout(() => {
                redirectUser(user);
            }, 1700);
        } catch (error) {
            console.error(
                "LOGIN ERROR:",
                error
            );

            const responseData =
                error?.response?.data;

            if (responseData) {
                const handled =
                    handleBlockedResponse(
                        responseData
                    );

                if (handled) {
                    return;
                }
            }

            Swal.fire({
                icon: "error",
                title: "Login Failed",
                text: getLoginError(error),
                confirmButtonText: "Try Again",
                confirmButtonColor: COLORS.navyBlue,
                background: COLORS.white,
                color: COLORS.text,
            });
        } finally {
            setLoading(false);
        }
    };

    /* =====================================================
       INPUT STYLES
    ===================================================== */

    const inputStyles = {
        "& .MuiOutlinedInput-root": {
            minHeight: 57,
            borderRadius: "13px",
            background: "#FFFDFC",

            "& fieldset": {
                borderColor: COLORS.border,
            },

            "&:hover fieldset": {
                borderColor: "#C9B56A",
            },

            "&.Mui-focused fieldset": {
                borderColor: COLORS.navyBlue,
                borderWidth: 1.5,
            },
        },

        "& input": {
            fontSize: ".9rem",
        },
    };

    /* =====================================================
       PAGE
    ===================================================== */

    return (
        <Box
            sx={{
                minHeight: "100dvh",
                width: "100%",
                background: COLORS.cream,
                overflow: "hidden",
            }}
        >
            <Box
                sx={{
                    minHeight: "100dvh",
                    display: {
                        xs: "block",
                        md: "grid",
                    },
                    gridTemplateColumns: {
                        md: "1.12fr .88fr",
                    },
                }}
            >
                {/* =====================================================
                    LEFT — PATIF INSTITUTIONAL EXPERIENCE
                ===================================================== */}

                <Box
                    sx={{
                        display: {
                            xs: "none",
                            md: "flex",
                        },
                        position: "relative",
                        minHeight: "100dvh",
                        overflow: "hidden",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        px: {
                            md: 6,
                            lg: 8,
                            xl: 10,
                        },
                        py: 5,
                        color: COLORS.white,
                        background:
                            "linear-gradient(135deg, #021426 0%, #031B35 48%, #062A4B 100%)",
                    }}
                >
                    <InstitutionalGrid />

                    <Glow
                        size={680}
                        top={-360}
                        left={-320}
                        color="rgba(246,201,68,.12)"
                    />

                    <Glow
                        size={540}
                        bottom={-300}
                        right={-270}
                        color="rgba(15,109,171,.18)"
                    />

                    <FloatingInstitutionIcon
                        Icon={School}
                        top="20%"
                        left="9%"
                        delay={0}
                        size={32}
                    />

                    <FloatingInstitutionIcon
                        Icon={WorkspacePremium}
                        top="33%"
                        right="11%"
                        delay={1.2}
                        size={30}
                    />

                    <FloatingInstitutionIcon
                        Icon={Lightbulb}
                        top="51%"
                        left="8%"
                        delay={2.3}
                        size={29}
                    />

                    <FloatingInstitutionIcon
                        Icon={BusinessCenter}
                        top="65%"
                        right="10%"
                        delay={3.4}
                        size={30}
                    />

                    <FloatingInstitutionIcon
                        Icon={Groups}
                        top="75%"
                        left="22%"
                        delay={4.5}
                        size={28}
                    />

                    <FloatingInstitutionIcon
                        Icon={MenuBook}
                        top="43%"
                        left="43%"
                        delay={5.5}
                        size={27}
                    />

                    {/* =================================================
                        HEADER
                    ================================================= */}

                    <Box
                        sx={{
                            position: "relative",
                            zIndex: 10,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 52,
                                    height: 52,
                                    p: .5,
                                    borderRadius: "14px",
                                    background:
                                        COLORS.white,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    boxShadow:
                                        "0 14px 32px rgba(0,0,0,.25)",
                                    overflow: "hidden",
                                }}
                            >
                                <Box
                                    component="img"
                                    src={BRAND_LOGO}
                                    alt="PASSION AIDS THE FUTURE"
                                    sx={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                        borderRadius: "10px",
                                    }}
                                />
                            </Box>

                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: ".7rem",
                                        fontWeight: 950,
                                        letterSpacing: 1.7,
                                        color:
                                            COLORS.gold,
                                    }}
                                >
                                    PATIF
                                </Typography>

                                <Typography
                                    sx={{
                                        fontSize: ".76rem",
                                        fontWeight: 800,
                                        letterSpacing: .8,
                                        color:
                                            "rgba(255,255,255,.82)",
                                    }}
                                >
                                    PASSION AIDS THE FUTURE
                                </Typography>
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                px: 1.6,
                                py: .75,
                                borderRadius: "30px",
                                border:
                                    "1px solid rgba(246,201,68,.24)",
                                background:
                                    "rgba(246,201,68,.07)",
                                backdropFilter:
                                    "blur(12px)",
                            }}
                        >
                            <Box
                                sx={{
                                    width: 7,
                                    height: 7,
                                    borderRadius: "50%",
                                    background:
                                        COLORS.gold,
                                    boxShadow:
                                        "0 0 13px rgba(246,201,68,.8)",
                                }}
                            />

                            <Typography
                                sx={{
                                    fontSize: ".58rem",
                                    fontWeight: 900,
                                    letterSpacing: 1,
                                    color:
                                        "rgba(255,255,255,.75)",
                                }}
                            >
                                INSTITUTIONAL PORTAL
                            </Typography>
                        </Box>
                    </Box>

                    {/* =================================================
                        MAIN CONTENT
                    ================================================= */}

                    <Box
                        sx={{
                            position: "relative",
                            zIndex: 10,
                            maxWidth: 720,
                            mt: -4,
                        }}
                    >
                        <Typography
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: .8,
                                px: 1.7,
                                py: .8,
                                mb: 2.5,
                                borderRadius: "7px",
                                background:
                                    "rgba(246,201,68,.10)",
                                border:
                                    "1px solid rgba(246,201,68,.25)",
                                color: COLORS.gold,
                                fontSize: ".62rem",
                                fontWeight: 950,
                                letterSpacing: 1.7,
                            }}
                        >
                            <WorkspacePremium
                                sx={{
                                    fontSize: 15,
                                }}
                            />
                            COLLEGE OF PROFESSIONAL & VOCATIONAL STUDIES
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: {
                                    md: "3.45rem",
                                    lg: "4.25rem",
                                    xl: "4.8rem",
                                },
                                fontWeight: 1000,
                                lineHeight: .98,
                                letterSpacing: "-2.8px",
                            }}
                        >
                            Passion.
                            <br />

                            <Box
                                component="span"
                                sx={{
                                    color: COLORS.gold,
                                }}
                            >
                                Education.
                            </Box>

                            <br />

                            Opportunity.
                        </Typography>

                        <Typography
                            sx={{
                                maxWidth: 610,
                                mt: 3,
                                color:
                                    "rgba(255,255,255,.68)",
                                fontSize: ".98rem",
                                lineHeight: 1.85,
                            }}
                        >
                            PASSION AIDS THE FUTURE provides
                            accessible, practical and professional
                            education that transforms passion into
                            knowledge, knowledge into practical skills,
                            and skills into meaningful professional
                            opportunities.
                        </Typography>

                        {/* =================================================
                            PHILOSOPHY
                        ================================================= */}

                        <Box
                            sx={{
                                mt: 3.5,
                                p: 2.1,
                                maxWidth: 640,
                                borderRadius: "15px",
                                background:
                                    "rgba(255,255,255,.045)",
                                border:
                                    "1px solid rgba(255,255,255,.10)",
                                backdropFilter:
                                    "blur(10px)",
                            }}
                        >
                            <Typography
                                sx={{
                                    mb: 1.1,
                                    fontSize: ".6rem",
                                    fontWeight: 950,
                                    letterSpacing: 1.6,
                                    color: COLORS.gold,
                                }}
                            >
                                OUR PHILOSOPHY
                            </Typography>

                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    flexWrap: "wrap",
                                    gap: .65,
                                }}
                            >
                                {[
                                    "Passion",
                                    "Education",
                                    "Skills",
                                    "Leadership",
                                    "Opportunity",
                                    "A Better Future",
                                ].map(
                                    (
                                        item,
                                        index
                                    ) => (
                                        <React.Fragment
                                            key={item}
                                        >
                                            <Box
                                                sx={{
                                                    px: 1.15,
                                                    py: .7,
                                                    borderRadius:
                                                        "8px",
                                                    background:
                                                        index ===
                                                        5
                                                            ? "rgba(246,201,68,.15)"
                                                            : "rgba(255,255,255,.06)",
                                                    border:
                                                        index ===
                                                        5
                                                            ? "1px solid rgba(246,201,68,.30)"
                                                            : "1px solid rgba(255,255,255,.08)",
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize:
                                                            ".67rem",
                                                        fontWeight:
                                                            800,
                                                        color:
                                                            index ===
                                                            5
                                                                ? COLORS.gold
                                                                : "rgba(255,255,255,.78)",
                                                    }}
                                                >
                                                    {item}
                                                </Typography>
                                            </Box>

                                            {index <
                                                5 && (
                                                <ArrowForward
                                                    sx={{
                                                        fontSize:
                                                            14,
                                                        color:
                                                            "rgba(246,201,68,.65)",
                                                    }}
                                                />
                                            )}
                                        </React.Fragment>
                                    )
                                )}
                            </Box>
                        </Box>

                        {/* =================================================
                            FOCUS AREAS
                        ================================================= */}

                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns:
                                    "repeat(2, minmax(0, 1fr))",
                                gap: 1.1,
                                maxWidth: 540,
                                mt: 2.5,
                            }}
                        >
                            {[
                                {
                                    icon: MenuBook,
                                    label: "Professional Education",
                                },
                                {
                                    icon: WorkspacePremium,
                                    label: "Vocational Skills",
                                },
                                {
                                    icon: Groups,
                                    label: "Leadership Development",
                                },
                                {
                                    icon: BusinessCenter,
                                    label: "Entrepreneurship",
                                },
                            ].map(
                                ({
                                    icon: Icon,
                                    label,
                                }) => (
                                    <Box
                                        key={label}
                                        sx={{
                                            display:
                                                "flex",
                                            alignItems:
                                                "center",
                                            gap: 1.15,
                                            px: 1.5,
                                            py: 1.15,
                                            borderRadius:
                                                "10px",
                                            background:
                                                "rgba(255,255,255,.035)",
                                            border:
                                                "1px solid rgba(255,255,255,.075)",
                                        }}
                                    >
                                        <Icon
                                            sx={{
                                                fontSize: 19,
                                                color:
                                                    COLORS.gold,
                                            }}
                                        />

                                        <Typography
                                            sx={{
                                                fontSize:
                                                    ".68rem",
                                                fontWeight:
                                                    800,
                                                color:
                                                    "rgba(255,255,255,.78)",
                                            }}
                                        >
                                            {label}
                                        </Typography>
                                    </Box>
                                )
                            )}
                        </Box>
                    </Box>

                    {/* =================================================
                        FOOTER
                    ================================================= */}

                    <Box
                        sx={{
                            position: "relative",
                            zIndex: 10,
                            display: "flex",
                            alignItems: "center",
                            justifyContent:
                                "space-between",
                            borderTop:
                                "1px solid rgba(255,255,255,.10)",
                            pt: 2.5,
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: ".61rem",
                                fontWeight: 800,
                                letterSpacing: 1.1,
                                color:
                                    "rgba(255,255,255,.43)",
                            }}
                        >
                            PASSION • EDUCATION • SKILLS
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: ".61rem",
                                fontWeight: 800,
                                color:
                                    "rgba(255,255,255,.43)",
                            }}
                        >
                            A BETTER FUTURE
                        </Typography>
                    </Box>
                </Box>

                {/* =====================================================
                    RIGHT — LOGIN AREA
                ===================================================== */}

                <Box
                    sx={{
                        position: "relative",
                        minHeight: "100dvh",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                        px: {
                            xs: 2,
                            sm: 4,
                            md: 5,
                            lg: 7,
                        },
                        py: {
                            xs: 5,
                            md: 5,
                        },
                        background:
                            "linear-gradient(145deg, #FFFDF8 0%, #F8F5EA 100%)",
                    }}
                >
                    <Glow
                        size={420}
                        top={-240}
                        right={-220}
                        color="rgba(246,201,68,.11)"
                    />

                    <Glow
                        size={340}
                        bottom={-210}
                        left={-190}
                        color="rgba(6,58,107,.09)"
                    />

                    {/* =================================================
                        MOBILE BRAND
                    ================================================= */}

                    <Box
                        sx={{
                            display: {
                                xs: "flex",
                                md: "none",
                            },
                            position: "absolute",
                            top: 17,
                            left: 18,
                            right: 18,
                            alignItems: "center",
                            justifyContent:
                                "space-between",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: .9,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 38,
                                    height: 38,
                                    p: .35,
                                    borderRadius: "10px",
                                    background:
                                        COLORS.white,
                                    border:
                                        `1px solid ${COLORS.border}`,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent:
                                        "center",
                                    overflow: "hidden",
                                    boxShadow:
                                        "0 5px 16px rgba(3,27,53,.08)",
                                }}
                            >
                                <Box
                                    component="img"
                                    src={BRAND_LOGO}
                                    alt="PATIF"
                                    sx={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                        borderRadius: "7px",
                                    }}
                                />
                            </Box>

                            <Typography
                                sx={{
                                    fontSize: ".65rem",
                                    fontWeight: 950,
                                    letterSpacing: .7,
                                    color:
                                        COLORS.navy,
                                }}
                            >
                                PASSION AIDS THE FUTURE
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                px: 1.2,
                                py: .55,
                                borderRadius: "20px",
                                background:
                                    COLORS.paleGold,
                                color:
                                    COLORS.orange,
                                fontSize: ".56rem",
                                fontWeight: 950,
                                letterSpacing: .5,
                            }}
                        >
                            PATIF
                        </Box>
                    </Box>

                    {/* =================================================
                        LOGIN CONTENT
                    ================================================= */}

                    <motion.div
                        initial={{
                            opacity: 0,
                            x: 30,
                        }}
                        animate={{
                            opacity: 1,
                            x: 0,
                        }}
                        transition={{
                            duration: .7,
                            ease: "easeOut",
                        }}
                        style={{
                            position: "relative",
                            zIndex: 10,
                            width: "100%",
                            maxWidth: 455,
                        }}
                    >
                        {/* =================================================
                            LOGO + TITLE
                        ================================================= */}

                        <Box
                            sx={{
                                textAlign: "center",
                                mb: 3,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 76,
                                    height: 76,
                                    mx: "auto",
                                    mb: 2,
                                    p: .7,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent:
                                        "center",
                                    borderRadius: "21px",
                                    background:
                                        COLORS.white,
                                    border:
                                        `1px solid ${COLORS.border}`,
                                    boxShadow:
                                        "0 18px 40px rgba(3,27,53,.13)",
                                    position: "relative",
                                    overflow: "hidden",
                                }}
                            >
                                <Box
                                    component="img"
                                    src={BRAND_LOGO}
                                    alt="PASSION AIDS THE FUTURE"
                                    sx={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                        borderRadius:
                                            "15px",
                                    }}
                                />

                                <Box
                                    sx={{
                                        position:
                                            "absolute",
                                        width: 14,
                                        height: 14,
                                        right: -3,
                                        top: -3,
                                        borderRadius:
                                            "50%",
                                        background:
                                            COLORS.gold,
                                        border:
                                            `3px solid ${COLORS.cream}`,
                                    }}
                                />
                            </Box>

                            <Typography
                                sx={{
                                    fontSize: ".64rem",
                                    fontWeight: 950,
                                    letterSpacing: 1.8,
                                    color:
                                        COLORS.orange,
                                }}
                            >
                                PASSION AIDS THE FUTURE
                            </Typography>

                            <Typography
                                sx={{
                                    mt: .75,
                                    fontSize: {
                                        xs: "2rem",
                                        sm: "2.3rem",
                                    },
                                    fontWeight: 1000,
                                    letterSpacing:
                                        "-1.4px",
                                    color:
                                        COLORS.navy,
                                    lineHeight: 1.05,
                                }}
                            >
                                Welcome Back
                            </Typography>

                            <Typography
                                sx={{
                                    mt: 1,
                                    color:
                                        COLORS.muted,
                                    fontSize: ".86rem",
                                    lineHeight: 1.65,
                                }}
                            >
                                Sign in to access your
                                institutional portal.
                            </Typography>
                        </Box>

                        {/* =================================================
                            LOGIN CARD
                        ================================================= */}

                        <Box
                            sx={{
                                position: "relative",
                                p: {
                                    xs: 2.3,
                                    sm: 3.5,
                                },
                                borderRadius: "24px",
                                background:
                                    "rgba(255,255,255,.94)",
                                border:
                                    `1px solid ${COLORS.border}`,
                                boxShadow:
                                    "0 28px 75px rgba(3,27,53,.10)",
                                backdropFilter:
                                    "blur(18px)",
                            }}
                        >
                            {/* TOP GOLD LINE */}

                            <Box
                                sx={{
                                    position:
                                        "absolute",
                                    top: 0,
                                    left: "12%",
                                    right: "12%",
                                    height: 3,
                                    borderRadius:
                                        "0 0 8px 8px",
                                    background:
                                        `linear-gradient(
                                            90deg,
                                            ${COLORS.navyBlue},
                                            ${COLORS.gold},
                                            ${COLORS.orange}
                                        )`,
                                }}
                            />

                            {/* =================================================
                                CARD HEADER
                            ================================================= */}

                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems:
                                        "center",
                                    justifyContent:
                                        "space-between",
                                    mb: 2.5,
                                    pb: 2,
                                    borderBottom:
                                        `1px solid ${COLORS.border}`,
                                }}
                            >
                                <Box>
                                    <Typography
                                        sx={{
                                            fontSize:
                                                ".78rem",
                                            fontWeight:
                                                950,
                                            color:
                                                COLORS.navy,
                                            letterSpacing:
                                                .2,
                                        }}
                                    >
                                        MEMBER LOGIN
                                    </Typography>

                                    <Typography
                                        sx={{
                                            mt: .35,
                                            fontSize:
                                                ".67rem",
                                            color:
                                                COLORS.muted,
                                        }}
                                    >
                                        Secure access to your
                                        PATIF account
                                    </Typography>
                                </Box>

                                <Box
                                    sx={{
                                        display:
                                            "flex",
                                        alignItems:
                                            "center",
                                        gap: .6,
                                        px: 1,
                                        py: .6,
                                        borderRadius:
                                            "8px",
                                        background:
                                            COLORS.paleBlue,
                                    }}
                                >
                                    <VerifiedUser
                                        sx={{
                                            fontSize:
                                                14,
                                            color:
                                                COLORS.navyBlue,
                                        }}
                                    />

                                    <Typography
                                        sx={{
                                            fontSize:
                                                ".55rem",
                                            fontWeight:
                                                900,
                                            color:
                                                COLORS.navyBlue,
                                        }}
                                    >
                                        SECURE
                                    </Typography>
                                </Box>
                            </Box>

                            <form
                                onSubmit={
                                    handleSubmit
                                }
                            >
                                {/* =================================================
                                    EMAIL
                                ================================================= */}

                                <Box sx={{ mb: 2 }}>
                                    <Typography
                                        sx={{
                                            mb: .7,
                                            ml: .2,
                                            fontSize:
                                                ".67rem",
                                            fontWeight:
                                                900,
                                            color:
                                                COLORS.text,
                                            letterSpacing:
                                                .2,
                                        }}
                                    >
                                        EMAIL ADDRESS
                                    </Typography>

                                    <TextField
                                        fullWidth
                                        placeholder="Enter your email address"
                                        value={email}
                                        onChange={(e) =>
                                            setEmail(
                                                e.target.value
                                            )
                                        }
                                        required
                                        autoComplete="email"
                                        InputProps={{
                                            startAdornment:
                                                (
                                                    <InputAdornment position="start">
                                                        <Email
                                                            sx={{
                                                                fontSize:
                                                                    20,
                                                                color:
                                                                    COLORS.navyBlue,
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                        }}
                                        sx={
                                            inputStyles
                                        }
                                    />
                                </Box>

                                {/* =================================================
                                    PASSWORD
                                ================================================= */}

                                <Box sx={{ mb: 1 }}>
                                    <Box
                                        sx={{
                                            display:
                                                "flex",
                                            justifyContent:
                                                "space-between",
                                            alignItems:
                                                "center",
                                            mb: .7,
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                ml: .2,
                                                fontSize:
                                                    ".67rem",
                                                fontWeight:
                                                    900,
                                                color:
                                                    COLORS.text,
                                                letterSpacing:
                                                    .2,
                                            }}
                                        >
                                            PASSWORD
                                        </Typography>

                                        <Link
                                            to="/forgotPassword"
                                            style={{
                                                color:
                                                    COLORS.navyBlue,
                                                fontSize:
                                                    ".7rem",
                                                fontWeight:
                                                    850,
                                                textDecoration:
                                                    "none",
                                            }}
                                        >
                                            Forgot password?
                                        </Link>
                                    </Box>

                                    <TextField
                                        fullWidth
                                        placeholder="Enter your password"
                                        type={
                                            showPassword
                                                ? "text"
                                                : "password"
                                        }
                                        value={
                                            password
                                        }
                                        onChange={(e) =>
                                            setPassword(
                                                e.target.value
                                            )
                                        }
                                        required
                                        autoComplete="current-password"
                                        InputProps={{
                                            startAdornment:
                                                (
                                                    <InputAdornment position="start">
                                                        <Lock
                                                            sx={{
                                                                fontSize:
                                                                    20,
                                                                color:
                                                                    COLORS.navyBlue,
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),

                                            endAdornment:
                                                (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            onClick={() =>
                                                                setShowPassword(
                                                                    (
                                                                        prev
                                                                    ) =>
                                                                        !prev
                                                                )
                                                            }
                                                            edge="end"
                                                            aria-label={
                                                                showPassword
                                                                    ? "Hide password"
                                                                    : "Show password"
                                                            }
                                                        >
                                                            {showPassword ? (
                                                                <VisibilityOff
                                                                    sx={{
                                                                        fontSize:
                                                                            20,
                                                                        color:
                                                                            COLORS.muted,
                                                                    }}
                                                                />
                                                            ) : (
                                                                <Visibility
                                                                    sx={{
                                                                        fontSize:
                                                                            20,
                                                                        color:
                                                                            COLORS.muted,
                                                                    }}
                                                                />
                                                            )}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                        }}
                                        sx={
                                            inputStyles
                                        }
                                    />
                                </Box>

                                {/* =================================================
                                    LOGIN BUTTON
                                ================================================= */}

                                <Button
                                    type="submit"
                                    fullWidth
                                    disabled={
                                        loading
                                    }
                                    sx={{
                                        mt: 2.4,
                                        minHeight: 58,
                                        borderRadius:
                                            "13px",
                                        textTransform:
                                            "none",
                                        fontSize:
                                            ".9rem",
                                        fontWeight:
                                            950,
                                        color:
                                            COLORS.white,

                                        background:
                                            `linear-gradient(
                                                135deg,
                                                ${COLORS.deepNavy} 0%,
                                                ${COLORS.navyBlue} 55%,
                                                ${COLORS.blue} 100%
                                            )`,

                                        boxShadow:
                                            "0 15px 30px rgba(3,27,53,.19)",

                                        "&:hover":
                                            {
                                                background:
                                                    `linear-gradient(
                                                        135deg,
                                                        ${COLORS.navyBlue},
                                                        ${COLORS.blue}
                                                    )`,
                                                transform:
                                                    "translateY(-1px)",
                                                boxShadow:
                                                    "0 19px 38px rgba(3,27,53,.23)",
                                            },

                                        "&:disabled":
                                            {
                                                color:
                                                    COLORS.white,
                                                background:
                                                    "#9AA5AE",
                                            },

                                        transition:
                                            "all .25s ease",
                                    }}
                                >
                                    {loading ? (
                                        <Box
                                            sx={{
                                                display:
                                                    "flex",
                                                alignItems:
                                                    "center",
                                                gap: 1.2,
                                            }}
                                        >
                                            <CircularProgress
                                                size={20}
                                                thickness={
                                                    5
                                                }
                                                sx={{
                                                    color:
                                                        COLORS.white,
                                                }}
                                            />

                                            Signing you in...
                                        </Box>
                                    ) : (
                                        <Box
                                            sx={{
                                                display:
                                                    "flex",
                                                alignItems:
                                                    "center",
                                                justifyContent:
                                                    "center",
                                                gap: .9,
                                            }}
                                        >
                                            Enter Institutional Portal

                                            <ArrowForward
                                                sx={{
                                                    fontSize:
                                                        18,
                                                }}
                                            />
                                        </Box>
                                    )}
                                </Button>

                                {/* =================================================
                                    HOME BUTTON
                                ================================================= */}

                                <Button
                                    fullWidth
                                    variant="text"
                                    onClick={() =>
                                        navigate("/")
                                    }
                                    startIcon={
                                        <ArrowBack />
                                    }
                                    sx={{
                                        mt: 1.2,
                                        color:
                                            COLORS.muted,
                                        fontSize:
                                            ".78rem",
                                        fontWeight:
                                            800,
                                        textTransform:
                                            "none",

                                        "&:hover":
                                            {
                                                background:
                                                    COLORS.paleGold,
                                                color:
                                                    COLORS.navyBlue,
                                            },
                                    }}
                                >
                                    Return to Institutional Home
                                </Button>
                            </form>
                        </Box>

                        {/* =================================================
                            FOOTER
                        ================================================= */}

                        <Box
                            sx={{
                                mt: 2.5,
                                textAlign:
                                    "center",
                            }}
                        >
                            <Box
                                sx={{
                                    display:
                                        "flex",
                                    justifyContent:
                                        "center",
                                    alignItems:
                                        "center",
                                    gap: .7,
                                    mb: .8,
                                }}
                            >
                                <School
                                    sx={{
                                        fontSize: 14,
                                        color:
                                            COLORS.orange,
                                    }}
                                />

                                <Typography
                                    sx={{
                                        fontSize:
                                            ".59rem",
                                        fontWeight:
                                            900,
                                        letterSpacing:
                                            .65,
                                        color:
                                            COLORS.orange,
                                    }}
                                >
                                    PROFESSIONAL EDUCATION • VOCATIONAL SKILLS
                                </Typography>
                            </Box>

                            <Typography
                                sx={{
                                    fontSize:
                                        ".62rem",
                                    color:
                                        COLORS.muted,
                                    lineHeight:
                                        1.6,
                                }}
                            >
                                Passion → Education → Skills →
                                Leadership → Opportunity
                            </Typography>

                            <Typography
                                sx={{
                                    mt: .4,
                                    fontSize:
                                        ".59rem",
                                    color:
                                        "#98A1A8",
                                }}
                            >
                                PASSION AIDS THE FUTURE
                            </Typography>
                        </Box>
                    </motion.div>
                </Box>
            </Box>
        </Box>
    );
};

export default Login;